import type { Status1, Conflict1 } from '@mm-shared/types';
import type { Objective1 } from '@mm-shared/types/Objective1';
import type { Result1 } from '@mm-shared/types/Result1';
import type { Comment1 } from '@mm-shared/types/Comment1';
import { MilestoneMapInterval } from '@mm-shared/types/MilestoneMap1';
import { isCoarserInterval, isFinerInterval } from './zoom';
import {
  doIntervalsOverlap,
  isPointInInterval,
  isIntervalAContainedByIntervalB,
} from './comparisonUtils';
import { getIntervalEnd, getIntervalStart } from './intervalUtils';

/**
 * Helper function to check if two conflicts involve the same parties
 */
function haveSameParties(a: Conflict1, b: Conflict1): boolean {
  // Match on the core identifiers without maps. Technically, the same
  // "parties" to this conflict could be on multiple maps. But it's still
  // the same conflict. So, less is more here, leaving out the maps.
  return (
    a.partiesUuids.sourceObjective === b.partiesUuids.sourceObjective &&
    a.partiesUuids.sourceResult === b.partiesUuids.sourceResult &&
    a.partiesUuids.targetObjective === b.partiesUuids.targetObjective &&
    a.partiesUuids.targetResult === b.partiesUuids.targetResult
  );
}

/**
 * Determines if a status is an inchstone (smaller milestone)
 */
export function isInchstone(status: Status1): boolean {
  // TODO: Implement this correctly
  return true;
}

/**
 * Determines if a status is a milestone
 */
export function isMilestone(status: Status1): boolean {
  // TODO: Implement this correctly
  return true;
}

/**
 * Updates a status record when a conflict is detected
 */
export function updateStatusWithConflict(status: Status1, conflict: Conflict1): Status1 {
  // Check if this conflict already exists
  const existingConflict = (status.knownConflicts || []).find((known) =>
    haveSameParties(known, conflict),
  );

  if (existingConflict) {
    // If it exists, don't add it again
    return status;
  }

  const isFuture = new Date(status.forIntervalEnding) > new Date();

  return {
    ...status,
    // TODO review/revise. It should really be 'R' for milestone, 'r' for inchstone.
    // Only update the status code if the date is in the future. Otherwise, assume
    // the status was already set by users to whatever its supposed to be.
    statusCode: isFuture ? 'r' : status.statusCode,
    knownConflicts: [...(status.knownConflicts || []), conflict],
    lastUpdatedDate: new Date().toISOString(),
  };
}

/**
 * Updates a status record when a conflict is resolved
 */
export function updateStatusWithResolvedConflict(
  status: Status1,
  resolvedConflict: Conflict1,
): Status1 {
  // Remove matching conflict from knownConflicts and add to resolvedConflicts
  const newKnownConflicts = (status.knownConflicts || []).filter(
    (known) => !haveSameParties(known, resolvedConflict),
  );

  const isFuture = new Date(status.forIntervalEnding) > new Date();

  return {
    ...status,
    // Only change status back if there are no remaining conflicts AND the
    // date is in the future. (If the date has passed, leave it as-is. This is
    // probably reflective of however the user chose to record it.)
    statusCode: isFuture ? 'p' : status.statusCode,
    knownConflicts: newKnownConflicts,
    resolvedConflicts: [...(status.resolvedConflicts || []), resolvedConflict],
    lastUpdatedDate: new Date().toISOString(),
  };
}

/**
 * Checks if a status has any active conflicts
 */
export function hasActiveConflicts(status: Status1): boolean {
  return (status.knownConflicts || []).length > 0;
}

// --- Presentation helpers moved from StatusCell1.tsx ---

export function getBackgroundColor(
  statusCode: string,
  hasResults = false,
  hasKeyResults = false,
  mode = 'traditional',
): string {
  let backgroundColor = '';

  // First check basic status codes (g, y, r, p, ?).
  switch (statusCode) {
    case 'g':
      backgroundColor = 'bg-status-g';
      break;
    case 'r':
      backgroundColor = 'bg-status-r';
      break;
    case 'y':
      backgroundColor = 'bg-status-y';
      break;
    case 'p':
      backgroundColor = 'bg-status-p';
      break;
    case '?':
      backgroundColor = 'bg-status-tbd';
      break;
    case '':
      backgroundColor = 'bg-gray-50';
      break;
    default:
      throw new Error(`Invalid status code: ${statusCode}`);
  }

  // Traditional mode. Handle inchstone and milestone statuses
  // consistent with spreadsheet-based milestone maps through verion 4 for:
  // i, M, G, Y, R.
  let traditionalCode = '';

  // Handle inchstones and milestones in order of specificity
  if (statusCode === 'p') {
    if (hasResults && !hasKeyResults) {
      traditionalCode = 'i'; // Inchstone
    } else if (hasKeyResults) {
      traditionalCode = 'M'; // Milestone
    }
  }

  // Handle completed milestone statuses
  if (hasKeyResults) {
    if (statusCode === 'g') traditionalCode = 'G';
    if (statusCode === 'y') traditionalCode = 'Y';
    if (statusCode === 'r') traditionalCode = 'R';
  }

  // TODO: Revisit color palette and style.
  // At least add more contrast. Maybe change styling
  // all together, add svgs, or something fancy.
  switch (traditionalCode) {
    case 'G':
      backgroundColor = 'bg-status-G';
      break;
    case 'R':
      backgroundColor = 'bg-status-R';
      break;
    case 'Y':
      backgroundColor = 'bg-status-Y';
      break;
    case 'i':
      backgroundColor = 'bg-status-i';
      break;
    case 'M':
      backgroundColor = 'bg-status-M';
      break;
  }

  return backgroundColor;
}

export function getTriangleIndicatorClass(
  corner: 'topRight' | 'bottomRight',
  color: string,
): string {
  const cornerPositions = {
    topRight: 'before:top-0 before:right-0',
    bottomRight: 'after:bottom-0 after:right-0',
  };

  const colorClasses = {
    'red-500': {
      bottom: 'after:border-b-red-500 after:border-r-red-500',
      top: 'before:border-t-red-500 before:border-r-red-500',
    },
    'blue-500': {
      bottom: 'after:border-b-blue-500 after:border-r-blue-500',
      top: 'before:border-t-blue-500 before:border-r-blue-500',
    },
    'gray-100': {
      bottom: 'after:border-b-gray-100 after:border-r-gray-100',
      top: 'before:border-t-gray-100 before:border-r-gray-100',
    },
    'gray-200': {
      bottom: 'after:border-b-gray-200 after:border-r-gray-200',
      top: 'before:border-t-gray-200 before:border-r-gray-200',
    },
    'gray-300': {
      bottom: 'after:border-b-gray-300 after:border-r-gray-300',
      top: 'before:border-t-gray-300 before:border-r-gray-300',
    },
    'gray-400': {
      bottom: 'after:border-b-gray-400 after:border-r-gray-400',
      top: 'before:border-t-gray-400 before:border-r-gray-400',
    },
    'gray-500': {
      bottom: 'after:border-b-gray-500 after:border-r-gray-500',
      top: 'before:border-t-gray-500 before:border-r-gray-500',
    },
    'gray-900': {
      bottom: 'after:border-b-gray-900 after:border-r-gray-900',
      top: 'before:border-t-gray-900 before:border-r-gray-900',
    },
    'white-bordered': {
      bottom: 'after:border-b-white after:border-r-white',
      top: 'before:border-t-white before:border-r-white',
    },
    'status-G': {
      bottom: 'after:border-b-status-G after:border-r-status-G',
      top: 'before:border-t-status-G before:border-r-status-G',
    },
    // Add other colors as needed
  };

  const pseudoElement = corner === 'topRight' ? 'before' : 'after';

  return `relative ${cornerPositions[corner]} ${pseudoElement}:absolute ${pseudoElement}:w-0 ${pseudoElement}:h-0 ${
    corner === 'bottomRight'
      ? `after:border-b-8 after:border-r-8 ${colorClasses[color as keyof typeof colorClasses].bottom} after:border-l-8 after:border-l-transparent after:border-t-8 after:border-t-transparent`
      : `before:border-t-8 before:border-r-8 ${colorClasses[color as keyof typeof colorClasses].top} before:border-l-transparent before:border-l-8 before:border-b-transparent before:border-b-8`
  }`;
}

export function filterItemsForInterval<T extends { forIntervalEnding: string; deadline?: string }>(
  items: T[] | undefined,
  intervalStart: Date,
  intervalEnd: Date,
): T[] {
  if (!items) return [];

  return items.filter((item) => {
    // Always prefer deadline over forIntervalEnding
    const targetDate = item.deadline ? new Date(item.deadline) : new Date(item.forIntervalEnding);

    // Use consistent boundary handling: exclude start, include end
    return isPointInInterval(targetDate, { start: intervalStart, end: intervalEnd }, false, true);
  });
}

/**
 * Note: This is funny because it's telling us things we need to know
 * about a status cell, but not looking at the corresponding status record.
 * It seems like statusCode, hasConflict, etc might all be natural details to
 * return to. Review/Revise as this evolves.
 *
 * Returns { hasResults, hasKeyResults, hasComments } for a given cell
 */
export function getStatusCellState(
  objective: Objective1,
  interval: { start: Date; end: Date },
): { hasResults: boolean; hasKeyResults: boolean; hasComments: boolean } {
  const resultsForInterval = filterItemsForInterval<Result1>(
    objective.results,
    interval.start,
    interval.end,
  );
  const hasResults = resultsForInterval.length > 0;
  const hasKeyResults = resultsForInterval.some((r: Result1) => r.isKeyResult);

  // Use interval filtering for comments too, instead of exact string matching
  const commentsForInterval = filterItemsForInterval(
    objective.comments || [],
    interval.start,
    interval.end,
  );
  const hasComments = commentsForInterval.length > 0;

  return { hasResults, hasKeyResults, hasComments };
}

/**
 * Gets the latest status that falls within the given interval.
 * This is used when we need to find the most recent status within a specific time range.
 *
 * @param statuses Array of statuses to search through
 * @param interval The interval to find a status for
 * @returns The latest status within the interval, or undefined if none found
 */
export function getLatestStatusInInterval(
  statuses: Status1[],
  interval: { start: Date; end: Date },
): Status1 | undefined {
  // Find all statuses that fall within the interval
  const statusesInInterval = statuses.filter((s) => {
    const statusDate = new Date(s.forIntervalEnding);
    // Use consistent boundary handling: exclude start, include end
    return isPointInInterval(statusDate, interval, false, true);
  });

  // If we found any statuses, return the one with the latest end date
  if (statusesInInterval.length > 0) {
    return statusesInInterval.sort(
      (a, b) => new Date(b.forIntervalEnding).getTime() - new Date(a.forIntervalEnding).getTime(),
    )[0];
  }

  return undefined;
}

/**
 * Maps a status to an adjusted interval based on zoom level.
 * Returns the most appropriate status for the given interval.
 *
 * @param statuses Array of statuses to search through
 * @param adjustedInterval The interval to find a status for
 * @param originalInterval The original interval type of the map
 * @param zoomLevel The current zoom level
 * @returns The most appropriate status for this interval, or undefined if none found
 */
export function getStatusForAdjustedInterval(
  statuses: Status1[],
  adjustedInterval: { start: Date; end: Date },
  originalZoomLevel: MilestoneMapInterval,
  zoomLevel: MilestoneMapInterval,
): Status1 | undefined {
  // If zoom matches original interval, find the latest status within the interval
  if (originalZoomLevel === zoomLevel) {
    return getLatestStatusInInterval(statuses, adjustedInterval);
  }

  // For zooming out: find any status that is contained within the larger interval
  if (isCoarserInterval(zoomLevel, originalZoomLevel)) {
    // Get all statuses that are contained within the larger interval
    const containedStatuses = statuses.filter((status) => {
      const statusDate = new Date(status.forIntervalEnding);
      const statusStartDate = getIntervalStart(statusDate, originalZoomLevel);
      const statusInterval = {
        start: statusStartDate,
        end: statusDate,
      };
      // Use containment check instead of overlap
      return isIntervalAContainedByIntervalB(statusInterval, adjustedInterval);
    });

    // Return the most recent status if any found
    if (containedStatuses.length > 0) {
      return containedStatuses.sort(
        (a, b) => new Date(b.forIntervalEnding).getTime() - new Date(a.forIntervalEnding).getTime(),
      )[0];
    }
  }

  // For zooming in: inherit status from the parent interval
  if (isFinerInterval(zoomLevel, originalZoomLevel)) {
    // Pass originalZoomLevel as the parent interval type to ensure we get the full parent interval
    const childInterval = adjustedInterval;
    const parentZoomLevel = originalZoomLevel;
    return inheritStatusForChildFromParent(statuses, childInterval, parentZoomLevel);
  }

  return undefined;
}

/**
 * Determines the status that should be inherited by a child interval from its parent.
 *
 * For example: All the weeks in a quarter should be able to inherit status from
 * a quarterly map with a status for the entire quarter. A "parent" interval
 * in this example could be Q4. A "child" interval here could be the first week
 * of December.
 *
 * Visual examples:
 * ```
 * Example 1: Single overlapping status - returns that status
 * [start P]------------Parent Status----------------------------[end P]
 *                 [start C]------Child Interval------[end C]
 *                 [start I]--Inherited Status of P---[end I]
 *
 * Example 2: Multiple overlapping statuses - returns most recent
 * [start P1]----Status 1----[end P1]    [start P2]----Status 2----[end P2]
 *                 [start C]------Child Interval------[end C]
 *                 [start I]--Inherited Status of 2---[end I]
 *
 * Example 3: No overlapping statuses - returns undefined
 * [start P]------------Parent Status----------------------------[end P]
 *                                                                        [start C]------Child Interval------[end C]
 * ```
 *
 * @param parentStatuses - Array of parent statuses to check
 * @param childInterval - The child interval that needs a status
 * @param parentIntervalType - The interval type of the parent map (e.g., 'quarters', 'months', 'weeks')
 * @returns The most recent overlapping status or undefined if none found
 */
export function inheritStatusForChildFromParent(
  parentStatuses: Status1[],
  childInterval: { start: Date; end: Date },
  parentIntervalType: MilestoneMapInterval,
): Status1 | undefined {
  // For each parent status, create its interval and check for overlap
  const overlappingStatuses = parentStatuses.filter((status) => {
    // Get the end date of the parent status
    const statusEndDate = new Date(status.forIntervalEnding);

    // Calculate the start date of the parent status's interval using the parent's interval type
    // This ensures we get the full parent interval (e.g., entire quarter) regardless of zoom level
    const statusStartDate = getIntervalStart(statusEndDate, parentIntervalType);

    // Create the parent status's interval
    const parentInterval = {
      start: statusStartDate,
      end: statusEndDate,
    };

    // Check if the child interval overlaps with this parent status's interval
    return doIntervalsOverlap(childInterval, parentInterval);
  });

  // If we found any overlapping statuses, return the most recent one.
  // (See example 2 above for desired behavior with more than one overlapping status.)
  if (overlappingStatuses.length > 0) {
    return overlappingStatuses.sort(
      (a, b) => new Date(b.forIntervalEnding).getTime() - new Date(a.forIntervalEnding).getTime(),
    )[0];
  }

  return undefined;
}

export function getBackgroundColorForResultCircles(
  statusCode: string,
  hasResults = false,
  hasKeyResults = false,
): string {
  // Use default logic first
  const backgroundColor = getBackgroundColor(statusCode, hasResults, hasKeyResults);

  // For inchstones, background should be the same as planned work.
  // The inchstones (result circles) will be darker and indicate
  // that there's an inchstone here.
  if (statusCode === 'p' && hasResults && !hasKeyResults) {
    return 'bg-status-p';
  }

  return backgroundColor;
}
